import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import FormField from '../../../core/components/FormField.tsx';
import StringField from '../../../core/components/Fields/StringField.tsx';
import DateField from '../../../core/components/Fields/DateField.tsx';
import BooleanField from '../../../core/components/Fields/BooleanField.tsx';
import { useTranslation } from 'react-i18next';
import Button from '../../../core/components/Button.tsx';
import Page from '../../../core/components/Page/index.tsx';
import Layout from '../../../core/components/Layout.tsx';
import { useMutation, useQuery } from '@apollo/client';
import {
    CREATE_API_INTEGRATION,
    DELETE_API_INTEGRATION,
    GET_API_INTEGRATIONS,
    REFRESH_API_INTEGRATION,
    UPDATE_API_INTEGRATION,
} from '../../queries.ts';
import { GET_NAVIGATION_CHANNELS } from '../../../core/components/Navigation/internal/queries.ts';
import { useToaster } from '../../../core/components/Toast.tsx';
import SettingsFormCard from '../../../core/components/SettingsFormCard.tsx';
import classnames from '../../../core/utils/classnames.tsx';
import Table, { RowControlIconButton } from '../../../core/components/Table.tsx';
import { withIcon } from '../../../core/components/Icon.tsx';
import {
    faArrowRotateRight,
    faTrashCan,
    faPencil,
    faPlus,
    faArrowUpRightFromSquare,
} from '@fortawesome/pro-regular-svg-icons';
import SettingsNavigation from '../../../core/components/SettingsNavigation.tsx';
import Secret from '../../../core/components/Secret.tsx';
import Modal from '../../../core/components/Modal.tsx';
import CheckboxGroup from '../../../core/components/CheckboxGroup.tsx';
import { Link, useNavigate, useOutletContext, useParams } from 'react-router-dom';
import { url } from '../../../core/utils/link.ts';
import { Outlet } from 'react-router';
import {
    CUSTOMER_API_INTEGRATIONS_PATH,
    CUSTOMER_API_INTEGRATIONS_EDIT_PATH,
    CUSTOMER_API_INTEGRATIONS_ADD_PATH,
} from '../../constants.ts';
import Tooltip from '../../../core/components/Tooltip.tsx';
import { canOpenIntegrationsInAdmin, canUseSupportMode } from '../../../users/utils.ts';
import { useApplicationContext } from '../../../core/contexts/ApplicationContext.tsx';

const RefreshIcon = withIcon(faArrowRotateRight);
const DeleteIcon = withIcon(faTrashCan);
const EditIcon = withIcon(faPencil);
const AddIcon = withIcon(faPlus);
const ExternalLinkIcon = withIcon(faArrowUpRightFromSquare);

const FORM_DEFAULT_VALUES = {
    name: '',
    expiresAt: null,
    allChannels: false,
    orderChannels: [],
    orderConfirmationChannels: [],
    invoiceChannels: [],
    rfqChannels: [],
    deliveryNoteChannels: [],
    listOfServicesChannels: [],
    universalChannels: [],
};

interface ApiIntegrationFormModalOutletContext {
    integrations: any[];
    refetch: () => Promise<any>;
}

const ApiIntegrationFormModal = ({
    title,
    submitButtonLabel,
    onSubmit,
    onClose,
    integration,
}: {
    title: string;
    submitButtonLabel: string;
    onSubmit: (data: any) => void;
    onClose: () => void;
    integration?: any;
}) => {
    const { t } = useTranslation('integrations');

    const form = useForm({ defaultValues: FORM_DEFAULT_VALUES, mode: 'onTouched' });
    const formErrors = form.formState.errors;
    const allChannelsValue = form.watch('allChannels');

    useEffect(() => {
        if (integration) {
            form.reset({
                ...FORM_DEFAULT_VALUES,
                name: integration.name,
                expiresAt: integration.expiresAt ? integration.expiresAt.split('T')[0] : null,
                allChannels: integration.allChannels || false,
                orderChannels: integration.orderChannels.map((channel) => channel.id),
                orderConfirmationChannels: integration.orderConfirmationChannels.map((channel) => channel.id),
                invoiceChannels: integration.invoiceChannels.map((channel) => channel.id),
                rfqChannels: integration.rfqChannels.map((channel) => channel.id),
                deliveryNoteChannels: integration.deliveryNoteChannels.map((channel) => channel.id),
                listOfServicesChannels: integration.listOfServicesChannels.map((channel) => channel.id),
                universalChannels: integration.universalChannels.map((channel) => channel.id),
            });
        }
    }, [integration]);

    const { data: channelData } = useQuery(GET_NAVIGATION_CHANNELS, {
        fetchPolicy: 'network-only',
        notifyOnNetworkStatusChange: false,
    });

    const orderChannels = channelData?.orderProcessingChannels || [];
    const orderConfirmationChannels = channelData?.orderConfirmationProcessingChannels || [];
    const invoiceChannels = channelData?.invoiceProcessingChannels || [];
    const rfqChannels = channelData?.rfqProcessingChannels || [];
    const deliveryNoteChannels = channelData?.deliveryNoteProcessingChannels || [];
    const listOfServicesChannels = channelData?.listOfServicesProcessingChannels || [];
    const universalChannels = channelData?.universalProcessingChannels || [];

    return (
        <Modal
            visible={true}
            onClose={onClose}
            title={title}
            buttons={
                <>
                    <Button type="button" asChild>
                        <Link to={url(CUSTOMER_API_INTEGRATIONS_PATH)}>{t('apiIntegration.form.cancelButton')}</Link>
                    </Button>
                    <Button variant="primary" type="submit" form="modal-form">
                        {submitButtonLabel}
                    </Button>
                </>
            }
        >
            <form className="flex flex-col gap-6" onSubmit={form.handleSubmit(onSubmit)} id="modal-form">
                <Controller
                    name="name"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('apiIntegration.form.name')} error={formErrors.name}>
                            <StringField {...field} />
                        </FormField>
                    )}
                />

                <Controller
                    name="expiresAt"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('apiIntegration.form.expiresAt')} error={formErrors.expiresAt}>
                            <DateField {...field} onValueChange={(value) => field.onChange(value)} />
                        </FormField>
                    )}
                />

                <Controller
                    name="allChannels"
                    control={form.control}
                    render={({ field }) => (
                        <FormField label={t('apiIntegration.form.allChannels')} error={formErrors.allChannels}>
                            <BooleanField {...field} value={field.value ? 'true' : ''} />
                        </FormField>
                    )}
                />

                {!allChannelsValue && (
                    <>
                        <Controller
                            name="orderChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.orderChannels')}
                                    error={formErrors.orderChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            orderChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="orderConfirmationChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.orderConfirmationChannels')}
                                    error={formErrors.orderConfirmationChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            orderConfirmationChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="invoiceChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.invoiceChannels')}
                                    error={formErrors.invoiceChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            invoiceChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="rfqChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('apiIntegration.form.rfqChannels')} error={formErrors.rfqChannels}>
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            rfqChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="deliveryNoteChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.deliveryNoteChannels')}
                                    error={formErrors.deliveryNoteChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            deliveryNoteChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="listOfServicesChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.listOfServicesChannels')}
                                    error={formErrors.listOfServicesChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            listOfServicesChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />

                        <Controller
                            name="universalChannels"
                            control={form.control}
                            render={({ field }) => (
                                <FormField
                                    label={t('apiIntegration.form.universalChannels')}
                                    error={formErrors.universalChannels}
                                >
                                    <CheckboxGroup
                                        {...field}
                                        options={
                                            universalChannels.map((channel) => ({
                                                value: channel.id,
                                                label: channel.name,
                                            })) || []
                                        }
                                        onValueChange={(value) => field.onChange({ target: { value } })}
                                    />
                                </FormField>
                            )}
                        />
                    </>
                )}
            </form>
        </Modal>
    );
};

export const AddApiIntegrationFormModal = () => {
    const { t } = useTranslation('integrations');
    const { refetch } = useOutletContext<ApiIntegrationFormModalOutletContext>();

    const { publishToast } = useToaster();
    const navigate = useNavigate();

    const [createIntegration] = useMutation(CREATE_API_INTEGRATION);
    const handleCreateIntegration = async (formData) => {
        try {
            await createIntegration({
                variables: {
                    name: formData.name,
                    expiresAt: formData.expiresAt,
                    allChannels: formData.allChannels,
                    orderChannels: formData.orderChannels,
                    orderConfirmationChannels: formData.orderConfirmationChannels,
                    invoiceChannels: formData.invoiceChannels,
                    rfqChannels: formData.rfqChannels,
                    deliveryNoteChannels: formData.deliveryNoteChannels,
                    listOfServicesChannels: formData.listOfServicesChannels,
                    universalChannels: formData.universalChannels,
                },
            });
            await refetch();
            publishToast({ description: t('apiIntegration.createSuccess'), status: 'success' });
            navigate(url(CUSTOMER_API_INTEGRATIONS_PATH));
        } catch (error) {
            console.error(error);
            publishToast({ description: t('apiIntegration.createError'), status: 'error' });
        }
    };

    return (
        <ApiIntegrationFormModal
            title={t('apiIntegration.form.createTitle')}
            submitButtonLabel={t('apiIntegration.form.createButton')}
            onSubmit={handleCreateIntegration}
            onClose={() => navigate(url(CUSTOMER_API_INTEGRATIONS_PATH))}
        />
    );
};

const ApiIntegrationRow = ({
    integration,
    refetch,
    onRefreshedIntegrationChange,
}: {
    integration: any;
    refetch: any;
    onRefreshedIntegrationChange?: (integration: any) => void;
}) => {
    const { t, i18n } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const { user } = useApplicationContext();

    const [refreshIntegration] = useMutation(REFRESH_API_INTEGRATION);
    const handleRefreshIntegration = async () => {
        try {
            const refreshedResponse = await refreshIntegration({ variables: { apiIntegrationId: integration.id } });
            onRefreshedIntegrationChange(refreshedResponse?.data?.refreshApiIntegration?.apiIntegration);
            await refetch();
            publishToast({ description: t('apiIntegration.refreshSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('apiIntegration.refreshError'), status: 'error' });
        }
    };

    const [deleteIntegration] = useMutation(DELETE_API_INTEGRATION);
    const handleDeleteIntegration = async () => {
        try {
            await deleteIntegration({ variables: { apiIntegrationId: integration.id } });
            await refetch();
            publishToast({ description: t('apiIntegration.deleteSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('apiIntegration.deleteError'), status: 'error' });
        }
    };

    const allChannels = [
        ...integration.orderChannels,
        ...integration.orderConfirmationChannels,
        ...integration.invoiceChannels,
        ...integration.rfqChannels,
        ...integration.deliveryNoteChannels,
        ...integration.listOfServicesChannels,
        ...integration.universalChannels,
    ];

    const adminUrl = `${process.env.BACKEND_URL}admin/integrations/integration/${integration.id}/change/`;

    return (
        <>
            <Table.Row>
                <Table.Cell>{integration.name}</Table.Cell>
                <Table.Cell>
                    {integration.allChannels ? (
                        <span className="text-content-medium italic">{t('apiIntegration.allChannels')}</span>
                    ) : (
                        <div className="flex gap-1.5 flex-wrap">
                            {allChannels.map((channel) => (
                                <span
                                    key={channel.id}
                                    className="border-line-weak border border-solid bg-surface-strong flex gap-2 items-center px-1 rounded"
                                >
                                    {channel.name}
                                </span>
                            ))}
                        </div>
                    )}
                </Table.Cell>
                <Table.Cell>
                    <Secret
                        value={integration.key}
                        fallbackLength={32}
                        helpText={integration.key ? t('apiIntegration.secretOnlyShownOnce') : undefined}
                    />
                </Table.Cell>
                <Table.Cell>
                    {integration.expiresAt ? (
                        <span
                            className={new Date(integration.expiresAt) < new Date() ? 'text-content-danger-strong' : ''}
                        >
                            {new Date(integration.expiresAt).toLocaleString(i18n.language, {
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                            })}
                        </span>
                    ) : (
                        <span className="text-content-medium italic">{t('apiIntegration.noExpiration')}</span>
                    )}
                </Table.Cell>
                <Table.Cell>{new Date(integration.createdAt).toLocaleString(i18n.language)}</Table.Cell>
                <Table.Cell sticky="right">
                    <div className="flex gap-2">
                        <Tooltip content={t(`apiIntegration.refreshTooltip`)}>
                            <RowControlIconButton onClick={handleRefreshIntegration}>
                                <RefreshIcon />
                            </RowControlIconButton>
                        </Tooltip>

                        <Tooltip content={t(`apiIntegration.editTooltip`)}>
                            <RowControlIconButton asChild>
                                <Link
                                    to={url(CUSTOMER_API_INTEGRATIONS_EDIT_PATH, {
                                        integrationId: integration.id,
                                    })}
                                    className="!text-content-strong hover:!text-content-strong"
                                >
                                    <EditIcon />
                                </Link>
                            </RowControlIconButton>
                        </Tooltip>

                        <Tooltip content={t(`apiIntegration.deleteTooltip`)}>
                            <RowControlIconButton
                                className="hover:bg-surface-danger-weak text-content-danger-strong hover:text-content-danger-strong"
                                onClick={handleDeleteIntegration}
                            >
                                <DeleteIcon />
                            </RowControlIconButton>
                        </Tooltip>

                        {canOpenIntegrationsInAdmin(user) && (
                            <Tooltip content={t(`apiIntegration.openInAdminTooltip`)}>
                                <RowControlIconButton asChild>
                                    <a href={adminUrl} target="_blank">
                                        <ExternalLinkIcon />
                                    </a>
                                </RowControlIconButton>
                            </Tooltip>
                        )}
                    </div>
                </Table.Cell>
            </Table.Row>
        </>
    );
};

const ApiIntegrationsPage = () => {
    const { t } = useTranslation('integrations');
    const { publishToast } = useToaster();

    const form = useForm({ defaultValues: { name: '' }, mode: 'onTouched' });
    const formErrors = form.formState.errors;

    const [createdIntegration, setCreatedIntegration] = useState(null);

    const {
        data: integrationData,
        loading,
        refetch,
    } = useQuery(GET_API_INTEGRATIONS, {
        notifyOnNetworkStatusChange: true,
        fetchPolicy: 'no-cache',
    });
    const integrations = integrationData?.apiIntegrations || [];

    const [createIntegration] = useMutation(CREATE_API_INTEGRATION);
    const handleCreateIntegration = async (formData) => {
        try {
            const createdResponse = await createIntegration({ variables: formData });
            setCreatedIntegration(createdResponse?.data?.createApiIntegration?.apiIntegration);
            await refetch();
            form.reset({ name: '' });
            publishToast({ description: t('apiIntegration.createSuccess'), status: 'success' });
        } catch (e) {
            console.error(e);
            publishToast({ description: t('apiIntegration.createError'), status: 'error' });
        }
    };

    return (
        <Layout>
            <SettingsNavigation />

            <Page className="flex-1">
                <Page.Header className="justify-between">
                    <Page.HeaderTitle>{t('apiIntegration.title')}</Page.HeaderTitle>

                    <div className="flex gap-2">
                        <Page.HeaderButton asChild>
                            <Link to={url(CUSTOMER_API_INTEGRATIONS_ADD_PATH)}>
                                <AddIcon />
                                {t('apiIntegration.addButton')}
                            </Link>
                        </Page.HeaderButton>
                    </div>
                </Page.Header>

                <Page.Content lowered className="flex flex-col gap-6 relative">
                    <Table scrollable className={classnames('w-full table-auto', loading && 'opacity-50')}>
                        <Table.Head>
                            <Table.Row>
                                <Table.HeadCell className="min-w-96 w-96">
                                    {t('apiIntegration.table.name')}
                                </Table.HeadCell>
                                <Table.HeadCell>{t('apiIntegration.table.channels')}</Table.HeadCell>
                                <Table.HeadCell>{t('apiIntegration.table.key')}</Table.HeadCell>
                                <Table.HeadCell>{t('apiIntegration.table.expiresAt')}</Table.HeadCell>
                                <Table.HeadCell>{t('apiIntegration.table.createdAt')}</Table.HeadCell>
                                <Table.HeadCell sticky="right" className="min-w-14 w-14" />
                            </Table.Row>
                        </Table.Head>
                        <Table.Body>
                            {integrations && integrations.length > 0 ? (
                                integrations.map((integration) => (
                                    <ApiIntegrationRow
                                        key={integration.id}
                                        integration={
                                            createdIntegration?.id === integration.id ? createdIntegration : integration
                                        }
                                        refetch={refetch}
                                        onRefreshedIntegrationChange={setCreatedIntegration}
                                    />
                                ))
                            ) : (
                                <Table.Row>
                                    <Table.Cell colSpan={6} className="text-center text-content-medium">
                                        {t('apiIntegration.noIntegrations')}
                                    </Table.Cell>
                                </Table.Row>
                            )}
                        </Table.Body>
                    </Table>

                    <SettingsFormCard
                        onSubmit={form.handleSubmit(handleCreateIntegration)}
                        title={t('apiIntegration.form.quickCreateTitle')}
                    >
                        <p className="text-content-strong text-sm">{t('apiIntegration.form.quickCreateDescription')}</p>

                        <Controller
                            name="name"
                            control={form.control}
                            render={({ field }) => (
                                <FormField label={t('apiIntegration.form.name')} error={formErrors.name}>
                                    <StringField {...field} placeholder={t('apiIntegration.form.namePlaceholder')} />
                                </FormField>
                            )}
                        />

                        <div className="flex justify-end">
                            <Button type="submit" variant="primary">
                                {t('apiIntegration.form.createButton')}
                            </Button>
                        </div>
                    </SettingsFormCard>
                </Page.Content>
            </Page>

            <Outlet context={{ integrations, refetch }} />
        </Layout>
    );
};

export default ApiIntegrationsPage;
